import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity,
  JaarruimteGegevens
} from "@snelstart-ventures/fiscaal/domain-logic";
import {debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, Subscription, take} from 'rxjs';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";

type JaarruimteFormGroup = FormGroup<{
  jaar: FormControl<number | null>;
  jaarruimte: FormControl<number | null>;
  winstUitOnderneming: FormControl<number | null>;
  toevoegingFor: FormControl<number | null>;
  afnemingFor: FormControl<number | null>;
  belastbaarLoon: FormControl<number | null>;
  belastbaarROW: FormControl<number | null>;
  belastbarePU: FormControl<number | null>;
  factorA: FormControl<number | null>;
  premiesNettoPensioen: FormControl<number | null>;
  afgetrokkenPremie: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-jaarruimtegegevens',
  templateUrl: './jaarruimtegegevens.component.html',
  styleUrls: ['./jaarruimtegegevens.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiDialogModule
  ]
})
export class JaarruimtegegevensComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private bokkzClient = inject(IbRekenService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public dialogVisible = false;
  public isPartner = false;
  public hasPremie = false;
  public isDefAangifte = {} as boolean;
  public jaar = 2024;
  public dialogJaar = 2024;
  public jaarRuimteJaar: SelectFormControlItem<number>[] = [];
  public reserveringsRuimtenJaren: SelectFormControlItem<number>[] = [];

  public formGroup = new FormGroup({
    jaarruimtegegevens: new FormArray<JaarruimteFormGroup>([]),
  });

  public formGroupRapport = new FormGroup({
    jaarruimte: new FormControl<number | null>(0),
    reserveringsruimteMinEen: new FormControl<number | null>(0),
    reserveringsruimteMinTwee: new FormControl<number | null>(0),
    reserveringsruimteMinDrie: new FormControl<number | null>(0),
    reserveringsruimteMinVier: new FormControl<number | null>(0),
    reserveringsruimteMinVijf: new FormControl<number | null>(0),
    reserveringsruimteMinZes: new FormControl<number | null>(0),
    reserveringsruimteMinZeven: new FormControl<number | null>(0),
    reserveringsruimteMinAcht: new FormControl<number | null>(0),
    reserveringsruimteMinNegen: new FormControl<number | null>(0),
    reserveringsruimteMinTien: new FormControl<number | null>(0),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        this.isDefAangifte = entity.taxType === 'DA';
        this.jaar = entity.year!;
        this.dialogJaar = this.jaar;
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.changeSubscriptions.unsubscribe();
    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
      this.getRapport(this.dataService.incomeTaxEntity.value.aangifte!);
    }
  }

  public getRapport(aangifte: Aangifte) {
    if (this.isPartner) {
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        this.formGroupRapport.patchValue(rapport.specificatieLijfrente?.opbouw || {}, {emitEvent: false});
        this.patchBerekendeJaarruimteInRows({emitEvent: true});
        this.saveForm();
      })
    } else {
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        this.formGroupRapport.patchValue(rapport.specificatieLijfrente?.opbouw || {}, {emitEvent: false});
        this.patchBerekendeJaarruimteInRows({emitEvent: true});
        this.saveForm();
      });
    }
  }

  private patchBerekendeJaarruimteInRows(options?: { emitEvent?: boolean }): void {
    const emitEvent = options?.emitEvent ?? false;

    this.jaarruimteGegevens.controls.forEach((group) => {
      const rowYear = group.controls.jaar.value;
      if (rowYear == null) {
        return;
      }

      if (this.hasSpecification(rowYear)) {
        const berekend = this.berekendeRuimte(rowYear);
        if (group.controls.jaarruimte.value !== berekend) {
          group.controls.jaarruimte.setValue(berekend, {emitEvent});
        }
      }
    });
  }

  private refreshReserveringsRuimtenOptionStates(): void {
    const selectedYears = new Set(
      this.jaarruimteGegevens.getRawValue()
        .map((item) => item.jaar)
        .filter((year): year is number => year != null)
    );

    this.reserveringsRuimtenJaren = this.reserveringsRuimtenJaren.map((option) => ({
      ...option,
      disabled: selectedYears.has(option.value),
    }));
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.jaarRuimteJaar = [];
    this.reserveringsRuimtenJaren = [];
    this.hasPremie = details.box1?.uitgavenVoorInkomensvoorzieningen?.lijfrentePremiesBetaald?.length > 0
    this.updateForm(details.box1?.uitgavenVoorInkomensvoorzieningen?.jaarruimteGegevensPerJaar || []);
    this.jaarRuimteJaar = [
      {
        value: details.aangifteJaar,
        label: 'Jaarruimte ' + details.aangifteJaar.toString(),
      },
    ];
    this.reserveringsRuimtenJaren = [
        {
            value: details.aangifteJaar - 10,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 10).toString(),
        },
        {
            value: details.aangifteJaar - 9,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 9).toString(),
        },
        {
            value: details.aangifteJaar - 8,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 8).toString(),
        },
        {
            value: details.aangifteJaar - 7,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 7).toString(),
        },
        {
            value: details.aangifteJaar - 6,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 6).toString(),
        },
        {
            value: details.aangifteJaar - 5,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 5).toString(),
        },
        {
            value: details.aangifteJaar - 4,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 4).toString(),
        },
        {
            value: details.aangifteJaar - 3,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 3).toString(),
        },
        {
            value: details.aangifteJaar - 2,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 2).toString(),
        },
        {
            value: details.aangifteJaar - 1,
            label: 'Reserveringsruimte ' + (details.aangifteJaar - 1).toString(),
        },
    ];
    this.patchBerekendeJaarruimteInRows();
    this.refreshReserveringsRuimtenOptionStates();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();
    this.subscribeToBedragChanges(this.jaarruimteGegevens);
  }

  private subscribeToBedragChanges(formArray: FormArray<JaarruimteFormGroup>): void {
    this.changeSubscriptions.add(
      formArray.valueChanges.pipe(
        debounceTime(500),
        map(() => formArray.getRawValue()),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.patchBerekendeJaarruimteInRows();
        this.refreshReserveringsRuimtenOptionStates();
        this.saveForm();
      })
    );
  }

  get jaarruimteGegevens(): FormArray<JaarruimteFormGroup> {
    return this.formGroup.controls.jaarruimtegegevens;
  }

  addRij(): void {
    this.jaarruimteGegevens.push(this.createFormGroup());
    this.patchBerekendeJaarruimteInRows();
    this.refreshReserveringsRuimtenOptionStates();
  }

  verwijderRij(index: number): void {
    this.jaarruimteGegevens.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.refreshReserveringsRuimtenOptionStates();
    this.saveForm();
  }

  createFormGroup(item?: JaarruimteGegevens): FormGroup {
    return new FormGroup({
      jaar: new FormControl<number | null>(item?.jaar ?? null),
      jaarruimte: new FormControl<number | null>(item?.jaarruimte ?? null),
      winstUitOnderneming: new FormControl<number | null>(item?.winstUitOnderneming ?? null),
      toevoegingFor: new FormControl<number | null>(item?.toevoegingFor ?? null),
      afnemingFor: new FormControl<number | null>(item?.afnemingFor ?? null),
      belastbaarLoon: new FormControl<number | null>(item?.belastbaarLoon ?? null),
      belastbaarROW: new FormControl<number | null>(item?.belastbaarROW ?? null),
      belastbarePU: new FormControl<number | null>(item?.belastbarePU ?? null),
      factorA: new FormControl<number | null>(item?.factorA ?? null),
      premiesNettoPensioen: new FormControl<number | null>(item?.premiesNettoPensioen ?? null),
      afgetrokkenPremie: new FormControl<number | null>(item?.afgetrokkenPremie ?? null),
    });
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogJaar = this.jaarruimteGegevens.at(index).controls.jaar.value ?? this.jaar;
    this.dialogVisible = true;
  }

  berekendeRuimte(year: number): number {
    const verschil = this.jaar - year;

    const sleutelOpVerschil: Array<keyof typeof this.formGroupRapport.controls> = [
      'jaarruimte',
      'reserveringsruimteMinEen',
      'reserveringsruimteMinTwee',
      'reserveringsruimteMinDrie',
      'reserveringsruimteMinVier',
      'reserveringsruimteMinVijf',
      'reserveringsruimteMinZes',
      'reserveringsruimteMinZeven',
      'reserveringsruimteMinAcht',
      'reserveringsruimteMinNegen',
      'reserveringsruimteMinTien',
    ];

    if (verschil < 0 || verschil >= sleutelOpVerschil.length) {
      return 0;
    }

    const controlName = sleutelOpVerschil[verschil];
    return this.formGroupRapport.controls[controlName].value ?? 0;
  }

  private updateForm(data: JaarruimteGegevens[]): void {
    let lijst = data || [];
    const formArray = this.jaarruimteGegevens;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i],);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  hasSpecification(year: number) {
    return this.jaarruimteGegevens.getRawValue()
      .filter((item) => item.jaar === year)
      .some((item) =>
        item.winstUitOnderneming != null ||
        item.toevoegingFor != null ||
        item.afnemingFor != null ||
        item.belastbaarLoon != null ||
        item.belastbaarROW != null ||
        item.belastbarePU != null ||
        item.factorA != null ||
        item.premiesNettoPensioen != null ||
        item.afgetrokkenPremie != null
      );
  }

  saveForm(): void {
    const gegevens: JaarruimteGegevens[] = this.jaarruimteGegevens.getRawValue()
      .filter(item => !(
        item.jaar == null &&
        item.jaarruimte == null &&
        item.winstUitOnderneming == null &&
        item.toevoegingFor == null &&
        item.afnemingFor == null &&
        item.belastbaarLoon == null &&
        item.belastbaarROW == null &&
        item.belastbarePU == null &&
        item.factorA == null &&
        item.premiesNettoPensioen == null &&
        item.afgetrokkenPremie == null
      ))
      .map((item, index) => ({
        jaar: item.jaar ?? this.jaarRuimteJaar[0]?.value ?? this.jaar,
        jaarruimte: index > 0 && item.afgetrokkenPremie != null ? undefined : item.jaarruimte ?? undefined,
        winstUitOnderneming: item.winstUitOnderneming ?? undefined,
        toevoegingFor: item.toevoegingFor ?? undefined,
        afnemingFor: item.afnemingFor ?? undefined,
        belastbaarLoon: item.belastbaarLoon ?? undefined,
        belastbaarROW: item.belastbaarROW ?? undefined,
        belastbarePU: item.belastbarePU ?? undefined,
        factorA: item.factorA ?? undefined,
        premiesNettoPensioen: item.premiesNettoPensioen ?? undefined,
        afgetrokkenPremie: item.afgetrokkenPremie ?? undefined,
      } as JaarruimteGegevens));

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          uitgavenVoorInkomensvoorzieningen: {
            ...aangifte.box1?.uitgavenVoorInkomensvoorzieningen,
            jaarruimteGegevensPerJaar: gegevens,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
